/**
 * @class SoulogMarquee  —  <soulog-marquee>
 * Generic seamless horizontal marquee: accumulates the offset itself with rAF instead of a CSS animation.
 *
 * Usage (track must be the first child, group must be the first child of track):
 *
 *   <soulog-marquee class="x__marquee" data-speed="45" data-direction="left" data-item=".x__item">
 *     <div class="x__track">
 *       <ul class="x__group"> …content… </ul>
 *       <ul class="x__group" aria-hidden="true"> …the same content… </ul>
 *     </div>
 *   </soulog-marquee>
 *
 *   data-speed     Seconds for one group to scroll through (larger = slower), default 45
 *   data-direction "left" (default, content moves left) | "right"
 *   data-item      Optional. If given, the item under the pointer gets .is-active (see the second note below)
 *
 * ── Why CSS animation can't be used (measured 2026-07-25, don't change it back) ──
 * In Safari, at the instant an animation resumes after being paused, the compositing layer position and the animation timeline disagree, and the whole strip jumps hundreds of px sideways.
 * Two trigger paths hit the same WebKit behavior: resuming from a hover pause (animation-play-state), and
 * switching away from the tab and back (the browser implicitly pauses/resumes). Measured jumps: hover 257px / tab switch 176px; Chrome doesn't have this problem.
 * Isolation testing ruled out mask, translate3d, will-change, the percentage offset basis, and DOM structure — every
 * CSS approach still jumps; only taking time control into JS is clean (0 jumps under the same conditions).
 * The key point is discarding any single-frame dt over 100ms: no time is accumulated while the tab is away, so on return it picks up exactly where it was.
 *
 * ── The highlighted item is also decided here, not left to CSS :hover (measured 2026-07-27, don't change it back) ──
 * Safari only re-evaluates :hover [when a pointer event occurs]; the element moving by itself (we write transform every frame) doesn't trigger a recalculation.
 * At the instant the mouse enters the strip the elements are still scrolling, so :hover hits whichever item was under the pointer then; right after, we pause the scroll here,
 * the elements freeze at a new position, the pointer hasn't moved, and Safari doesn't recalculate — so the highlight stays on the wrong item until the mouse moves again
 * to correct it. Chrome re-hit-tests after the compositing layer position changes, so only Safari shows the problem.
 * Fix: track the pointer coordinates ourselves, use elementFromPoint to determine the hit item and add .is-active,
 * and recompute once more [after the pause takes effect] — at that moment the position has just frozen, yet no pointer event will come to trigger a recalculation.
 */
if (!customElements.get("soulog-marquee")) {
	class SoulogMarquee extends HTMLElement {
		connectedCallback() {
			this.track = this.firstElementChild;
			this.group = this.track ? this.track.firstElementChild : null;
			if (!this.track || !this.group) return;

			// Time for one group to scroll through
			this.duration = (parseFloat(this.dataset.speed) || 45) * 1000;
			this.reverse = this.dataset.direction === "right";
			this.itemSelector = this.dataset.item || "";
			this.offset = 0;
			this.groupWidth = 0;
			this.lastTs = null;
			this.paused = false;
			this.rafId = null;
			this.pointer = null; // Last known pointer position (viewport coordinates)
			this.activeItem = null;

			this.hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
			this.motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");

			this.onFrame = (ts) => this.frame(ts);
			// Pause while the mouse rests on the strip, otherwise content slides out from under the pointer and can't be clicked at all.
			// Only active on devices that can truly hover, using the same gate as the CSS hover media query.
			this.onEnter = (event) => {
				if (!this.hoverMq.matches) return;
				this.paused = true;
				this.pointer = { x: event.clientX, y: event.clientY };
				// The position hasn't frozen yet this frame; determine the hit item on the next frame (see file header comment)
				requestAnimationFrame(() => this.syncActive());
			};
			this.onMove = (event) => {
				if (!this.hoverMq.matches) return;
				this.pointer = { x: event.clientX, y: event.clientY };
				this.syncActive();
			};
			this.onLeave = () => {
				this.paused = false;
				this.pointer = null;
				this.setActive(null);
			};
			this.onMotionChange = () => this.setup();

			this.addEventListener("pointerenter", this.onEnter);
			this.addEventListener("pointermove", this.onMove);
			this.addEventListener("pointerleave", this.onLeave);
			if (this.motionMq.addEventListener) {
				this.motionMq.addEventListener("change", this.onMotionChange);
			} else if (this.motionMq.addListener) {
				this.motionMq.addListener(this.onMotionChange);
			}

			this.setup();
		}

		setup() {
			this.teardownObservers();

			// User prefers reduced motion: don't run at all, leave static layout to the CSS reduced-motion branch
			if (this.motionMq.matches) {
				this.stop();
				this.track.style.transform = "";
				return;
			}

			// Width is measured only when it changes (images finished loading / viewport change / settings changed in the theme editor), not by reading layout every frame
			if ("ResizeObserver" in window) {
				this.ro = new ResizeObserver(() => this.measure());
				this.ro.observe(this.group);
			}
			this.measure();

			// Stop rAF once scrolled out of the viewport so we don't burn CPU for nothing
			if ("IntersectionObserver" in window) {
				this.io = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
					},
					{ rootMargin: "200px 0px" },
				);
				this.io.observe(this);
			} else {
				this.start();
			}
		}

		measure() {
			const width = this.group.getBoundingClientRect().width;
			if (!width) return;
			this.groupWidth = width;
			// When the width changes, fold the offset into the new group width, otherwise a seam would show
			this.offset = this.offset % width;
		}

		/** Determine the hit item from the current pointer coordinates — this is the sole basis for "which item is highlighted" */
		syncActive() {
			if (!this.itemSelector || !this.pointer) return;
			const hit = document.elementFromPoint(this.pointer.x, this.pointer.y);
			const item = hit && hit.closest ? hit.closest(this.itemSelector) : null;
			// There may be multiple marquees on the page; only accept items within this one
			this.setActive(item && this.contains(item) ? item : null);
		}

		setActive(item) {
			if (this.activeItem === item) return;
			if (this.activeItem) this.activeItem.classList.remove("is-active");
			if (item) item.classList.add("is-active");
			this.activeItem = item;
		}

		frame(ts) {
			this.rafId = requestAnimationFrame(this.onFrame);
			const width = this.groupWidth;
			if (!width) {
				this.lastTs = ts;
				return;
			}
			if (this.lastTs !== null) {
				let dt = ts - this.lastTs;
				// Tab switched away / long dropped frames: don't make up this time, so on return it picks up where it was (see file header comment)
				if (dt > 100) dt = 0;
				if (!this.paused) {
					this.offset = (this.offset + (width / this.duration) * dt) % width;
				}
			}
			this.lastTs = ts;
			// track holds two identical groups of content; any offset within [-groupWidth, 0] is seamless
			const tx = this.reverse ? this.offset - width : -this.offset;
			this.track.style.transform = "translate3d(" + tx + "px, 0, 0)";
		}

		start() {
			if (this.rafId !== null) return;
			this.lastTs = null; // Restarting doesn't make up the time of this gap
			this.rafId = requestAnimationFrame(this.onFrame);
		}

		stop() {
			if (this.rafId === null) return;
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
			this.lastTs = null;
		}

		teardownObservers() {
			if (this.io) {
				this.io.disconnect();
				this.io = null;
			}
			if (this.ro) {
				this.ro.disconnect();
				this.ro = null;
			}
		}

		disconnectedCallback() {
			this.stop();
			this.teardownObservers();
			this.setActive(null);
			this.removeEventListener("pointerenter", this.onEnter);
			this.removeEventListener("pointermove", this.onMove);
			this.removeEventListener("pointerleave", this.onLeave);
			if (this.motionMq) {
				if (this.motionMq.removeEventListener) {
					this.motionMq.removeEventListener("change", this.onMotionChange);
				} else if (this.motionMq.addListener) {
					this.motionMq.removeListener(this.onMotionChange);
				}
			}
		}
	}
	customElements.define("soulog-marquee", SoulogMarquee);
}
